import React, { useState } from 'react';
import { X, CheckCircle, XCircle, AlertCircle, FileText } from 'lucide-react';
import { REASON_TEMPLATES } from './FilterPanel';

export default function BulkActionModal({ 
  selectedRequests, 
  actionType, 
  onConfirm, 
  onCancel, 
  processing 
}) {
  const [customReason, setCustomReason] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [error, setError] = useState('');

  const isApproval = actionType === 'approve';
  const templates = isApproval ? REASON_TEMPLATES?.approval : REASON_TEMPLATES?.rejection;

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCustomReason(template);
    setError('');
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!customReason?.trim()) {
      setError('Please provide a reason or select a template');
      return;
    }

    if (!isApproval && customReason?.trim()?.length < 20) {
      setError('Please provide a detailed reason for rejection (minimum 20 characters)');
      return;
    }

    setError('');
    onConfirm(customReason);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isApproval ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex items-center gap-3">
            {isApproval ? (
              <CheckCircle className="w-6 h-6 text-green-600" />
            ) : (
              <XCircle className="w-6 h-6 text-red-600" />
            )}
            <h2 className="text-xl font-bold text-gray-900">
              Bulk {isApproval ? 'Approval' : 'Rejection'}
            </h2>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
              {selectedRequests?.length} requests
            </span>
          </div>
          <button
            onClick={onCancel}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Selected Requests Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Selected Requests</h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {selectedRequests?.map((request, index) => (
                <div key={request?.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-200 last:border-0">
                  <span className="text-gray-700">
                    {index + 1}. {request?.event?.title}
                  </span>
                  <span className="text-gray-500">
                    {request?.participant?.fullName || request?.participantName}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Reason Templates */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <FileText className="w-4 h-4 inline mr-1" />
              Quick Templates (Click to Use)
            </label>
            <div className="grid gap-2">
              {templates?.map((template, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleTemplateSelect(template)}
                  className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                    selectedTemplate === template
                      ? 'border-blue-500 bg-blue-50 text-blue-900' :'border-gray-200 hover:border-blue-300 bg-white text-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                      selectedTemplate === template
                        ? 'border-blue-500 bg-blue-500' :'border-gray-300'
                    }`}>
                      {selectedTemplate === template && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-sm">{template}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Reason */}
          <div>
            <label htmlFor="customReason" className="block text-sm font-medium text-gray-700 mb-2">
              {isApproval ? 'Approval Notes' : 'Rejection Reason'} (Customize or Write Your Own)
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              id="customReason"
              value={customReason}
              onChange={(e) => {
                setCustomReason(e?.target?.value);
                setError('');
              }}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
              placeholder={
                isApproval
                  ? 'Add custom notes or modify template...'
                  : 'Provide a clear reason for rejection (will be sent to all participants)...'
              }
              disabled={processing}
              required
            />
            {!isApproval && (
              <p className="text-xs text-gray-500 mt-1">
                Minimum 20 characters. This will be sent to all selected participants.
              </p>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Warning */}
          <div className={`${isApproval ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'} border rounded-lg p-4 flex items-start gap-2`}>
            <AlertCircle className={`w-5 h-5 ${isApproval ? 'text-blue-600' : 'text-orange-600'} flex-shrink-0 mt-0.5`} />
            <div className={`text-sm ${isApproval ? 'text-blue-800' : 'text-orange-800'}`}>
              <p className="font-medium mb-2">
                This action will {isApproval ? 'approve' : 'reject'} {selectedRequests?.length} cancellation request(s):
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                {isApproval ? (
                  <>
                    <li>Cancel all selected registrations</li>
                    <li>Process refunds for all participants</li>
                    <li>Free up event slots</li>
                    <li>Send confirmation emails</li>
                  </>
                ) : (
                  <>
                    <li>Reject all selected cancellation requests</li>
                    <li>Keep participants registered</li>
                    <li>Send rejection emails with your reason</li>
                    <li>Participants remain responsible for attending</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={processing}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className={`flex-1 px-6 py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isApproval
                  ? 'bg-green-600 hover:bg-green-700' :'bg-red-600 hover:bg-red-700'
              }`}
            >
              {processing 
                ? 'Processing...' 
                : `Confirm ${isApproval ? 'Approval' : 'Rejection'} (${selectedRequests?.length})`
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}